import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { Issue, Priority, Project, Status } from '../types';
import IssueCard from '../components/IssueCard';
import FilterBar from '../components/FilterBar';
import EmptyState from '../components/EmptyState';
import InviteMemberModal from '../components/InviteMemberModal';
import CloseIssueModal, { CloseProof } from '../components/CloseIssueModal';

interface ProjectMember {
  user_id: string;
  role: string;
  joined_at: string;
  full_name: string | null;
  email: string;
}

interface PendingInvite {
  id: string;
  email: string;
  role: string;
  created_at: string;
  expires_at: string;
}

const ROLE_COLOR: Record<string, string> = {
  developer: '#3b82f6',
  tester:    '#f59e0b',
  viewer:    '#6b7280',
  admin:     '#1C9B82',
};

function avatarInitial(member: { full_name: string | null; email: string }) {
  return ((member.full_name ?? member.email) || '?').charAt(0).toUpperCase();
}

function avatarBg(userId: string) {
  const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#f97316'];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

export default function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const { role, user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [project,         setProject]         = useState<Project | null>(null);
  const [issues,          setIssues]          = useState<Issue[]>([]);
  const [members,         setMembers]         = useState<ProjectMember[]>([]);
  const [pendingInvites,  setPendingInvites]  = useState<PendingInvite[]>([]);
  const [loading,         setLoading]         = useState(true);
  const [statusFilter,    setStatusFilter]    = useState<Status | 'All'>('All');
  const [priorityFilter,  setPriorityFilter]  = useState<Priority | 'All'>('All');
  const [searchQuery,     setSearchQuery]     = useState('');
  const [updatingIssueId, setUpdatingIssueId] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [removingId,      setRemovingId]      = useState<string | null>(null);
  const [revokingId,      setRevokingId]      = useState<string | null>(null);
  const [pendingCloseId,  setPendingCloseId]  = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!projectId) return;

    try {
      const memberRows = await api.get<ProjectMember[]>('/api/projects/' + projectId + '/members');
      setMembers(memberRows ?? []);
    } catch {
      setMembers([]);
    }

    try {
      const inviteRows = await api.get<PendingInvite[]>('/api/projects/' + projectId + '/invitations');
      setPendingInvites(inviteRows ?? []);
    } catch {
      setPendingInvites([]);
    }
  }, [projectId]);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);

    try {
      const [proj, fetchedIssues] = await Promise.all([
        api.get<Project>('/api/projects/' + projectId),
        api.get<Issue[]>('/api/issues?project_id=' + projectId),
      ]);

      setProject(proj);
      setIssues(fetchedIssues ?? []);
    } catch {
      showToast('Project not found.', 'error');
      navigate('/');
      return;
    }

    setLoading(false);
    fetchMembers();
  }, [projectId, showToast, navigate, fetchMembers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isProjectCreator = project?.created_by === user?.id;
  const isMember         = members.some(m => m.user_id === user?.id);
  const canCreate        = isProjectCreator || role === 'admin' || isMember;
  const canManageTeam    = role === 'admin' || isProjectCreator;
  const canAssign        = canManageTeam;

  const memberMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    members.forEach(m => { map[m.user_id] = m.full_name ?? m.email; });
    return map;
  }, [members]);

  const handleAssign = async (
    issueId: string,
    field: 'assigned_developer_id' | 'assigned_tester_id',
    userId: string | null,
  ) => {
    try {
      const updated = await api.patch<Issue>('/api/issues/' + issueId, {
        [field]: userId || null,
        updated_at: new Date().toISOString(),
      });
      setIssues(prev => prev.map(i => (i.id === issueId ? updated : i)));
      const label = field === 'assigned_developer_id' ? 'Developer' : 'QA';
      const name  = userId ? (memberMap[userId] ?? 'Member') : 'nobody';
      showToast(`${label} assigned to ${name}.`, 'success');
    } catch {
      showToast('Failed to assign.', 'error');
    }
  };

  const filtered = issues.filter(issue => {
    if (statusFilter   !== 'All' && issue.status   !== statusFilter)   return false;
    if (priorityFilter !== 'All' && issue.priority !== priorityFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!issue.title.toLowerCase().includes(q) && !issue.description?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const orderedIssues = [...filtered].sort((a, b) => {
    const aClosed = a.status === 'Closed';
    const bClosed = b.status === 'Closed';
    if (aClosed !== bClosed) return aClosed ? 1 : -1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const counts = {
    open:       issues.filter(i => i.status === 'Open').length,
    inProgress: issues.filter(i => i.status === 'In Progress').length,
    closed:     issues.filter(i => i.status === 'Closed').length,
  };

  const canUpdateIssueStatus = (issue: Issue) =>
    role === 'admin' ||
    user?.id === issue.assigned_developer_id ||
    (role === 'tester' && (user?.id === issue.assigned_tester_id || issue.review_status === 'ready_for_review'));

  const handleInlineStatusChange = async (issueId: string, status: Status) => {
    const current = issues.find((i) => i.id === issueId);
    if (!current || current.status === status || updatingIssueId) return;
    if (!canUpdateIssueStatus(current)) {
      showToast('You are not allowed to update this issue status.', 'error');
      return;
    }

    // Intercept: require close proof before marking Closed
    if (status === 'Closed' && current.status !== 'Closed') {
      setPendingCloseId(issueId);
      return;
    }

    setUpdatingIssueId(issueId);
    try {
      const updated = await api.patch<Issue>('/api/issues/' + issueId, {
        status,
        updated_at: new Date().toISOString(),
      });
      setIssues((prev) => prev.map((i) => (i.id === issueId ? updated : i)));
      showToast(`Issue moved to "${status}".`, 'success');
    } catch {
      showToast('Failed to update issue status.', 'error');
    }
    setUpdatingIssueId(null);
  };

  const handleCloseConfirm = async (proof: CloseProof) => {
    const issueId = pendingCloseId!;
    setPendingCloseId(null);

    setUpdatingIssueId(issueId);
    try {
      const updated = await api.patch<Issue>('/api/issues/' + issueId, {
        status:            'Closed',
        close_reason:      proof.reason || null,
        close_proof_image: proof.imageUrl || null,
        closed_at:         new Date().toISOString(),
        closed_by:         user!.id,
        updated_at:        new Date().toISOString(),
      });
      setIssues((prev) => prev.map((i) => (i.id === issueId ? updated : i)));
      showToast('Issue closed with proof recorded.', 'success');
    } catch {
      showToast('Failed to close issue.', 'error');
    }
    setUpdatingIssueId(null);
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!window.confirm(`Remove ${memberName} from this project?`)) return;
    setRemovingId(memberId);
    try {
      await api.delete('/api/projects/' + projectId + '/members/' + memberId);
      setMembers(prev => prev.filter(m => m.user_id !== memberId));
      showToast(`${memberName} removed from project.`, 'success');
    } catch {
      showToast('Failed to remove member.', 'error');
    }
    setRemovingId(null);
  };

  const handleRevokeInvite = async (inviteId: string, email: string) => {
    if (!window.confirm(`Revoke invitation for ${email}?`)) return;
    setRevokingId(inviteId);
    try {
      await api.patch('/api/projects/' + projectId + '/invitations/' + inviteId, { status: 'expired' });
      setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
      showToast(`Invitation for ${email} revoked.`, 'success');
    } catch {
      showToast('Failed to revoke invitation.', 'error');
    }
    setRevokingId(null);
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading-grid">{[1,2,3,4].map(i => <div key={i} className="issue-card-skeleton" />)}</div>
      </div>
    );
  }

  const hasTeamContent = members.length > 0 || pendingInvites.length > 0;
  // company_name is returned directly on the project object by the API
  const companyName = (project as any)?.company_name ?? null;

  return (
    <div className="page project-detail-page">
      {/* Close Proof Modal */}
      <CloseIssueModal
        isOpen={pendingCloseId !== null}
        issueTitle={issues.find(i => i.id === pendingCloseId)?.title ?? ''}
        onConfirm={handleCloseConfirm}
        onCancel={() => setPendingCloseId(null)}
      />

      {/* Breadcrumb */}
      <div className="project-breadcrumb">
        <Link to="/" className="breadcrumb-link">Projects</Link>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className="breadcrumb-current">{project?.name}</span>
      </div>

      {/* Header */}
      <div className="dashboard-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>{project?.name}</h1>
            {project?.visibility === 'private' ? (
              <span className="project-private-badge project-private-badge-lg">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                Private
              </span>
            ) : (
              <span className="project-public-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
                </svg>
                Public
              </span>
            )}
            {companyName && (
              <span className="project-company-badge project-company-badge-lg" title={`Owned by ${companyName}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                  <rect x="2" y="7" width="20" height="14" rx="2"/>
                  <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                </svg>
                {companyName}
              </span>
            )}
          </div>
          {project?.description && <p className="page-subtitle">{project.description}</p>}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {canManageTeam && (
            <button className="btn btn-secondary" onClick={() => setShowInviteModal(true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <line x1="19" y1="8" x2="19" y2="14"/>
                <line x1="22" y1="11" x2="16" y2="11"/>
              </svg>
              Invite
            </button>
          )}
          {canCreate && (
            <Link to={`/projects/${projectId}/issues/create`} className="btn btn-primary">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New Issue
            </Link>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-card stat-open"     onClick={() => setStatusFilter('Open')}        style={{ cursor: 'pointer' }}><div className="stat-count">{counts.open}</div><div className="stat-label">Open</div></div>
        <div className="stat-card stat-progress" onClick={() => setStatusFilter('In Progress')} style={{ cursor: 'pointer' }}><div className="stat-count">{counts.inProgress}</div><div className="stat-label">In Progress</div></div>
        <div className="stat-card stat-closed"   onClick={() => setStatusFilter('Closed')}      style={{ cursor: 'pointer' }}><div className="stat-count">{counts.closed}</div><div className="stat-label">Closed</div></div>
        <div className="stat-card"               onClick={() => setStatusFilter('All')}         style={{ cursor: 'pointer' }}><div className="stat-count">{issues.length}</div><div className="stat-label">Total</div></div>
      </div>

      {/* Team Panel */}
      {(hasTeamContent || canManageTeam) && (
        <div className="project-team-section">
          <div className="project-team-header">
            <h2 className="project-team-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 00-3-3.87"/>
                <path d="M16 3.13a4 4 0 010 7.75"/>
              </svg>
              Team
              {members.length > 0 && (
                <span className="project-team-count">{members.length}</span>
              )}
            </h2>
            {canManageTeam && (
              <button className="project-team-invite-btn" onClick={() => setShowInviteModal(true)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add member
              </button>
            )}
          </div>

          <div className="project-team-list">
            {members.map((m) => (
              <div key={m.user_id} className="project-team-member">
                <div
                  className="team-member-avatar"
                  style={{ background: avatarBg(m.user_id) }}
                  title={`${m.full_name ?? m.email} (${m.role})`}
                >
                  {avatarInitial(m)}
                </div>
                <div className="team-member-info">
                  <div className="team-member-name">{m.full_name ?? m.email}</div>
                  <div className="team-member-email">{m.full_name ? m.email : ''}</div>
                </div>
                <span className="team-member-role-badge" style={{ background: `${ROLE_COLOR[m.role] ?? '#6b7280'}18`, color: ROLE_COLOR[m.role] ?? '#6b7280' }}>
                  {m.role}
                </span>
                {canManageTeam && m.user_id !== user?.id && m.user_id !== project?.created_by && (
                  <button
                    className="team-member-remove-btn"
                    title="Remove member"
                    disabled={removingId === m.user_id}
                    onClick={() => handleRemoveMember(m.user_id, m.full_name ?? m.email)}
                  >
                    {removingId === m.user_id ? <span className="btn-spinner" style={{ width: 12, height: 12 }} /> : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    )}
                  </button>
                )}
              </div>
            ))}

            {/* Pending invitations */}
            {pendingInvites.map((inv) => (
              <div key={inv.id} className="project-team-member project-team-member-pending">
                <div className="team-member-avatar team-member-avatar-pending" title={`${inv.email} — invite pending`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <div className="team-member-info">
                  <div className="team-member-name">{inv.email}</div>
                  <div className="team-member-email team-pending-label">Invite pending</div>
                </div>
                <span className="team-member-role-badge" style={{ background: `${ROLE_COLOR[inv.role] ?? '#6b7280'}18`, color: ROLE_COLOR[inv.role] ?? '#6b7280' }}>
                  {inv.role}
                </span>
                {canManageTeam && (
                  <button
                    className="team-member-remove-btn"
                    title="Revoke invitation"
                    disabled={revokingId === inv.id}
                    onClick={() => handleRevokeInvite(inv.id, inv.email)}
                  >
                    {revokingId === inv.id ? <span className="btn-spinner" style={{ width: 12, height: 12 }} /> : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    )}
                  </button>
                )}
              </div>
            ))}

            {!hasTeamContent && canManageTeam && (
              <div className="team-empty-state">
                <p>No team members yet.</p>
                <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => setShowInviteModal(true)}>
                  Invite someone
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <FilterBar
        statusFilter={statusFilter}     onStatusChange={setStatusFilter}
        priorityFilter={priorityFilter} onPriorityChange={setPriorityFilter}
        searchQuery={searchQuery}       onSearchChange={setSearchQuery}
      />

      {/* Issues */}
      {filtered.length === 0 ? (
        issues.length === 0 ? (
          <EmptyState
            title="No issues yet"
            description={canCreate ? 'Create the first issue for this project.' : 'No issues have been reported yet.'}
            actionLabel={canCreate ? 'Create First Issue' : undefined}
            actionTo={canCreate ? `/projects/${projectId}/issues/create` : undefined}
          />
        ) : (
          <EmptyState
            title="No issues match"
            description="Try adjusting your filters."
            actionLabel="Clear Filters"
            onAction={() => { setStatusFilter('All'); setPriorityFilter('All'); setSearchQuery(''); }}
          />
        )
      ) : (
        <div className="issues-grid">
          {orderedIssues.map((issue) => (
            <IssueCard
              key={issue.id}
              issue={issue}
              canUpdateStatus={canUpdateIssueStatus(issue)}
              onStatusChange={handleInlineStatusChange}
              updatingStatus={updatingIssueId === issue.id}
              canAssign={canAssign}
              members={members}
              memberMap={memberMap}
              onAssign={handleAssign}
            />
          ))}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && project && (
        <InviteMemberModal
          projectId={projectId!}
          projectName={project.name}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => fetchMembers()}
        />
      )}
    </div>
  );
}
